// ─── Undo/Redo Hook ──────────────────────────────────────────
// Wraps a state value with history tracking.
// Debounces rapid changes (drag/resize) into single undo entries.
// Usage:
//   const [content, setContent, history] = useContentHistory({});
//   history.undo() / history.redo()
//   history.canUndo / history.canRedo

import { useState, useCallback, useRef } from 'react';

const MAX_HISTORY = 40;
const DEBOUNCE_MS = 400;

export function useContentHistory(initialValue) {
  const [state, rawSetState] = useState(initialValue);
  const pastRef = useRef([]);
  const futureRef = useRef([]);
  const pendingSnapshotRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const [, forceUpdate] = useState(0);

  // Debounced history commit — groups rapid changes (drag, resize) into one entry
  const commitSnapshot = useCallback(() => {
    if (pendingSnapshotRef.current !== null) {
      pastRef.current.push(pendingSnapshotRef.current);
      if (pastRef.current.length > MAX_HISTORY) pastRef.current.shift();
      futureRef.current = [];
      pendingSnapshotRef.current = null;
      forceUpdate(n => n + 1);
    }
  }, []);

  const setState = useCallback((updater) => {
    rawSetState(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;

      // On first change in a burst, snapshot the "before" state
      if (pendingSnapshotRef.current === null) {
        pendingSnapshotRef.current = JSON.parse(JSON.stringify(prev));
      }

      // Reset debounce timer — commit after DEBOUNCE_MS of no changes
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        commitSnapshot();
      }, DEBOUNCE_MS);

      return next;
    });
  }, [commitSnapshot]);

  // Force-commit any pending snapshot (call before save, etc.)
  const flush = useCallback(() => {
    clearTimeout(debounceTimerRef.current);
    commitSnapshot();
  }, [commitSnapshot]);

  const undo = useCallback(() => {
    // First flush any pending changes
    clearTimeout(debounceTimerRef.current);
    if (pendingSnapshotRef.current !== null) {
      commitSnapshot();
    }

    if (pastRef.current.length === 0) return;

    rawSetState(prev => {
      futureRef.current.push(JSON.parse(JSON.stringify(prev)));
      const restored = pastRef.current.pop();
      forceUpdate(n => n + 1);
      return restored;
    });
  }, [commitSnapshot]);

  const redo = useCallback(() => {
    if (futureRef.current.length === 0) return;

    rawSetState(prev => {
      pastRef.current.push(JSON.parse(JSON.stringify(prev)));
      const restored = futureRef.current.pop();
      forceUpdate(n => n + 1);
      return restored;
    });
  }, []);

  return [
    state,
    setState,
    {
      undo,
      redo,
      flush,
      canUndo: pastRef.current.length > 0,
      canRedo: futureRef.current.length > 0,
      historyLength: pastRef.current.length,
    },
  ];
}
